import React, {useEffect, useState, useCallback, useMemo} from "react";
import MetricsSection from "../../../components/MetricsSection";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Select, { SelectOption } from "@amzn/meridian/select";
import Chart from "../../reviewAndPublish/components/ChartContainer";
import ShiftTotalsSection from "../../../components/ShiftTotalsSection/ShiftTotalsSectionContainer";
import ProcessPathTabs from "../../reviewAndPublish/components/ProcessPathTabs";
import ShiftDataTable from "../../../components/ShiftDataTable";
import { SPMSGetShiftPlanResponse, PlanVersion } from "../../../types/shiftPlan";
import { getYesterdayDate } from "../models";
import PlanSummary from "../../reviewAndPublish/components/PlanSummary/PlanSummaryContainer";
import { buildGetShiftPlanOutputRequest, GetShiftPlanOutputRequest, ShiftSummaryCategory } from "../../reviewAndPublish/model";
import './index.scss';
import {createShiftPlan, getClockWindowNameBasedOnSelectedOption, getPlanTypeBasedOnSelectedTab} from "../../../services/shiftPlanService";
import {useAppDispatch} from "../../../store";
import {showNotification} from "../../../store/slices/errorSlice";
import {ErrorCode, PlanStatus} from "../../../utils/constants";
import {convertUTCToLocalTime} from "../../../utils/string-utils";

interface CurrentPlanSummaryProps {
    shiftPlans: SPMSGetShiftPlanResponse[];
    selectedTab: string;
    selectedDate: string;
    selectedShift: string;
    /** Called when the API signals a plan already exists — parent opens the modal */
    onNeedsTakeOver: (displayMessage: string) => void;
    /** Called on successful plan creation — parent handles dispatch + navigate */
    onPlanSuccess: (planId: string) => void;
}

interface CurrentPlanSummaryStateProps {
    planId: string | null;
    stationCode: string | null;
    shiftPlanOutput?: any; // GetShiftPlanOutputResponse from reviewAndPublish
}

interface CurrentPlanSummaryDispatchProps {
    getShiftPlanOutput: (getShiftPlanOutputRequest: GetShiftPlanOutputRequest) => void;
    showError: (errorPayload: { title: string; message: string }) => void;
}

type Props = CurrentPlanSummaryProps & CurrentPlanSummaryStateProps & CurrentPlanSummaryDispatchProps;

const CurrentPlanSummary: React.FC<Props> = ({
    shiftPlans,
    selectedTab,
    selectedDate,
    selectedShift,
    stationCode,
    shiftPlanOutput,
    getShiftPlanOutput,
    showError,
    onNeedsTakeOver,
    onPlanSuccess
}) => {
    const dispatch = useAppDispatch();
    const [selectedPlan, setSelectedPlan] = useState(shiftPlans[0]);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [userSelectedCategoryHeading, setUserSelectedCategoryHeading] = useState<string | undefined>(undefined);

    useEffect(() => {
        const fetchShiftPlanData = async () => {
            if (selectedPlan && selectedPlan.planId && selectedDate && stationCode && selectedShift) {
                await getShiftPlanOutput(buildGetShiftPlanOutputRequest(selectedPlan.planId as string, selectedDate, stationCode, selectedShift));
            }
        }
        console.log("Calling fetchShiftPlanData for selected plan: ", selectedPlan);
        fetchShiftPlanData();
    }, [selectedPlan]);

    // Calculate selected category using useMemo - runs during render, not after
    const selectedCategory = useMemo(() => {
        if (!shiftPlanOutput?.shiftSummary || shiftPlanOutput.shiftSummary.length === 0) {
            return undefined;
        }

        // If user has manually selected a category, try to find it in the new data
        if (userSelectedCategoryHeading) {
            const userChosenCategory = shiftPlanOutput.shiftSummary.find(
                (cat: ShiftSummaryCategory) => cat.heading === userSelectedCategoryHeading
            );
            if (userChosenCategory) {
                return userChosenCategory;
            }
        }

        // Default to first category if no user selection or if user's choice doesn't exist
        return shiftPlanOutput.shiftSummary[0];
    }, [shiftPlanOutput?.shiftSummary, userSelectedCategoryHeading]);

    // Validate shift plan output status - only for shiftPlanning page
    useEffect(() => {
        if (shiftPlanOutput && shiftPlanOutput.planStatus) {
            const status = shiftPlanOutput.planStatus;
            
            // Check if status is not valid (not PENDING_APPROVAL or APPROVED)
            if (status !== PlanStatus.PENDING_APPROVAL && status !== PlanStatus.APPROVED) {
                let errorMessage: string;
                
                if (status === PlanStatus.CLOSED) {
                    errorMessage = "This plan is closed. Please select any other valid plan";
                } else {
                    errorMessage = "This plan is not valid. Please select any other valid plan";
                }
                
                showError({
                    title: "Invalid Plan Status",
                    message: errorMessage
                });
            }
        }
    }, [shiftPlanOutput, showError]);

    // Handle tab change from ProcessPathTabs
    const handleTabChange = useCallback((categoryId: string) => {
        setUserSelectedCategoryHeading(categoryId);
    }, []);

    const handleSelectChange = (value: string) => {
        const selectedPlan = shiftPlans.find(
            plan => plan.updatedAt === value
        );
        setSelectedPlan(selectedPlan as SPMSGetShiftPlanResponse);
    }

    const onGeneratePlanClick = async () => {
        const userSelectedDate = new Date(selectedDate);
        const yesterdayDate = new Date(getYesterdayDate());
        if (userSelectedDate < yesterdayDate) {
            dispatch(showNotification({
                title: "Invalid Date",
                message: "Cannot generate plan for past date. Please try generating for another date.",
                type: "warning"
            }));
            return;
        }
        setIsGenerating(true);
        const response = await createShiftPlan(selectedDate,
            getClockWindowNameBasedOnSelectedOption(selectedShift), getPlanTypeBasedOnSelectedTab(selectedTab), PlanVersion.STATIC_V1);
        if (response.errorDetails?.errorCode === ErrorCode.PLAN_OWNED_BY_ANOTHER_USER) {
            onNeedsTakeOver(response.errorDetails.errorMessage); // trigger modal
        } else if (response.status && response.status !== 'FAILED' && response.planId) {
            onPlanSuccess(response.planId);
        }
        setIsGenerating(false);
    }

    const disableGenerateButton = !selectedDate || !selectedPlan || !selectedShift;
    return (
        <Box spacingInset="300" className="current-plan-summary">
            <Box className={"current-plan-header"}>
                <Box className="plan-versions">
                    <Select label={"Version"} value={selectedPlan.updatedAt as string} onChange={handleSelectChange}>
                        {shiftPlans.map((plan) => (
                            <SelectOption
                                key={plan.planId as string}
                                label={convertUTCToLocalTime(plan.updatedAt as string)}
                                value={plan.updatedAt as string}
                            />
                        ))}
                    </Select>
                </Box>
                <PlanSummary
                    onButtonClick={onGeneratePlanClick}
                    buttonLabel={isGenerating ? "Generating..." : "Start New Shift Plan"}
                    disableButton={disableGenerateButton}
                />
            </Box>

            <MetricsSection />

            <Box className="timeline">
                <Box type="outline" className="graph">
                    <Heading type="h500" level={1}>
                        RCC Recommended Volume
                    </Heading>
                    <Chart />
                </Box>
                <ShiftTotalsSection />
            </Box>

            <Box>
                <ProcessPathTabs 
                    shiftSummary={shiftPlanOutput?.shiftSummary}
                    onTabChange={handleTabChange}
                />
            </Box>

            <ShiftDataTable selectedCategory={selectedCategory} />
        </Box>
    )
}

export default CurrentPlanSummary;

import './index.scss';